import React from 'react';
import { Card, BlockStack, Text, InlineStack, Select } from '@shopify/polaris';
import { accountingHelpers } from '../../constants/accountingConstants';

/**
 * Simple Revenue Chart Component
 * Displays revenue trend in a simple bar chart format
 */
export const RevenueChart = ({ data, title = 'Revenue Trend', groupBy, onGroupByChange }) => {
    if (!data || data.length === 0) {
        return (
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                        {title}
                    </Text>
                    <Text tone="subdued">No revenue data available</Text>
                </BlockStack>
            </Card>
        );
    }

    // Find max revenue for scaling
    const maxRevenue = Math.max(...data.map(item => item.revenue || 0));

    const groupByOptions = [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Quarterly', value: 'quarterly' }
    ];

    return (
        <Card>
            <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingMd" as="h2">
                        {title}
                    </Text>
                    {onGroupByChange && (
                        <div style={{ minWidth: '150px' }}>
                            <Select
                                label=""
                                options={groupByOptions}
                                value={groupBy || 'monthly'}
                                onChange={onGroupByChange}
                            />
                        </div>
                    )}
                </InlineStack>

                <div style={{ padding: '1rem 0' }}>
                    {data.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}
                        >
                            <div style={{ minWidth: '100px', fontSize: '0.875rem' }}>
                                <Text variant="bodySm" tone="subdued">
                                    {accountingHelpers.formatAccountingPeriod(item.period) || item.period}
                                </Text>
                            </div>
                            <div style={{ flex: 1, position: 'relative' }}>
                                <div
                                    style={{
                                        height: '32px',
                                        backgroundColor: '#008060',
                                        borderRadius: '4px',
                                        width: `${(item.revenue / maxRevenue) * 100}%`,
                                        minWidth: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        paddingLeft: '8px',
                                        transition: 'width 0.3s ease'
                                    }}
                                >
                                    <Text variant="bodySm" tone="text-inverse">
                                        {accountingHelpers.formatCurrency(item.revenue)}
                                    </Text>
                                </div>
                            </div>
                            {item.order_count !== undefined && (
                                <div style={{ minWidth: '60px', textAlign: 'right' }}>
                                    <Text variant="bodySm" tone="subdued">
                                        {item.order_count} orders
                                    </Text>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div style={{
                    borderTop: '1px solid #e1e3e5',
                    paddingTop: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Text variant="bodyMd" fontWeight="semibold">
                        Total Revenue
                    </Text>
                    <Text variant="bodyMd" fontWeight="semibold">
                        {accountingHelpers.formatCurrency(
                            data.reduce((sum, item) => sum + (item.revenue || 0), 0)
                        )}
                    </Text>
                </div>
            </BlockStack>
        </Card>
    );
};

/**
 * Revenue vs Expense Chart Component
 */
export const RevenueVsExpenseChart = ({ revenueData, expenseData, title = 'Revenue vs Expenses' }) => {
    if (!revenueData || !expenseData || revenueData.length === 0) {
        return (
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                        {title}
                    </Text>
                    <Text tone="subdued">No data available</Text>
                </BlockStack>
            </Card>
        );
    }

    const maxValue = Math.max(
        ...revenueData.map(item => item.revenue || 0),
        ...expenseData.map(item => item.total_amount || 0)
    );

    return (
        <Card>
            <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                    {title}
                </Text>

                <div style={{ padding: '1rem 0' }}>
                    {revenueData.map((item, index) => {
                        const expense = expenseData.find(e => e.period === item.period) || { total_amount: 0 };
                        const profit = (item.revenue || 0) - (expense.total_amount || 0);

                        return (
                            <div
                                key={index}
                                style={{
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: index < revenueData.length - 1 ? '1px solid #e1e3e5' : 'none'
                                }}
                            >
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <Text variant="bodyMd" fontWeight="semibold">
                                        {accountingHelpers.formatAccountingPeriod(item.period) || item.period}
                                    </Text>
                                </div>

                                {/* Revenue Bar */}
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                        <Text variant="bodySm" tone="subdued">
                                            Revenue
                                        </Text>
                                    </div>
                                    <div
                                        style={{
                                            height: '24px',
                                            backgroundColor: '#008060',
                                            borderRadius: '4px',
                                            width: `${(item.revenue / maxValue) * 100}%`,
                                            minWidth: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingLeft: '8px'
                                        }}
                                    >
                                        <Text variant="bodySm" tone="text-inverse">
                                            {accountingHelpers.formatCurrency(item.revenue)}
                                        </Text>
                                    </div>
                                </div>

                                {/* Expense Bar */}
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                        <Text variant="bodySm" tone="subdued">
                                            Expenses
                                        </Text>
                                    </div>
                                    <div
                                        style={{
                                            height: '24px',
                                            backgroundColor: '#bf0711',
                                            borderRadius: '4px',
                                            width: `${(expense.total_amount / maxValue) * 100}%`,
                                            minWidth: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingLeft: '8px'
                                        }}
                                    >
                                        <Text variant="bodySm" tone="text-inverse">
                                            {accountingHelpers.formatCurrency(expense.total_amount)}
                                        </Text>
                                    </div>
                                </div>

                                {/* Profit */}
                                <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                                    <Text
                                        variant="bodySm"
                                        fontWeight="semibold"
                                        tone={accountingHelpers.getProfitLossColor(profit)}
                                    >
                                        Profit: {accountingHelpers.formatCurrency(profit)}
                                    </Text>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </BlockStack>
        </Card>
    );
};

export default RevenueChart;
